import { GameItemContent } from 'components/AlchemyContent/types/gameList'
import { RouteConfig } from 'config/constants/route'
import Column from 'layout/Components/Column'
import { RowCenter, RowMiddle } from 'layout/Components/Row'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Icons } from 'svgs'
import { colors } from 'theme/colors'
import Box from 'UIKit/Box/Box'
import { BoxProps, FlexProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image'
import Link from 'UIKit/Link'
import OpenEffect from 'UIKit/OpenEffect'
import Text from 'UIKit/Text'
import { formatRoute } from 'utils/stringHelper'

import useModal from 'hooks/useModal'
import Button from 'UIKit/Button'
import ShareGameModal from 'views/Games/components/ShareGameModal'

export interface GameItemActionsProps {
  game: GameItemContent
}

interface ShareGameButtonProps {
  gameSlug: string
  size: string
}

const ShareGameButton: React.FC<ShareGameButtonProps & BoxProps> = ({ gameSlug, size, ...props }) => {
  const [handlePresentShareGameModal] = useModal(ShareGameModal)

  return (
    <StyledShareButton
      variant="text"
      width="fit-content!important"
      height="fit-content !important"
      px="0px !important"
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        handlePresentShareGameModal({ gameSlug })
      }}
      {...props}
    >
      <Icons.ShareArrowIcon fill={colors.textSubtle} width={size} height={size} />
    </StyledShareButton>
  )
}

const GameInfo: React.FC<GameItemActionsProps> = ({ game }) => {
  return (
    <StyledWrapInfoGame p={['12px']}>
      <OpenEffect openType="grow" className="game-info">
        <Box>
          <Text ellipsis={1} letterSpacing="-2%" lineHeight="1.2" fontSize="16px" color="text" fontWeight={700}>
            {game.gameName}
          </Text>
          <Text fontSize="12px" color={`${colors.textAlt1} !important`} lineHeight="1.2" fontWeight={500}>
            {game.providerName}
          </Text>
        </Box>

        <RowMiddle mt="8px">
          <StyledPlayGame display="flex" width="32px" height="32px">
            <RowCenter mx="auto">
              <Icons.PlayAltIcon width="16px" height="16px" />
            </RowCenter>
          </StyledPlayGame>

          <ShareGameButton gameSlug={game.slug} size="24px" ml="8px" />
        </RowMiddle>
      </OpenEffect>
    </StyledWrapInfoGame>
  )
}

interface GameItemProps {
  game: GameItemContent
}

const GameItem: React.FC<GameItemProps & FlexProps> = ({ game, ...props }) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <StyledLink
      aria-label={game.gameName}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      href={formatRoute(RouteConfig.GameDetail, { slug: game.slug })}
    >
      <StyledWrapper {...props}>
        <StyledImgWrapper position="relative" width="100%" height="100%">
          <StyledImage width={320} height={320} src={game.imageUrl} alt={game.gameName} blurLoader />

          <StyledHoverWrapper style={{ opacity: isHovered ? 1 : 0 }}>
            {isHovered && (
              <Box position="absolute" top="0px" left="0px" bottom="0px" right="0px">
                <GameInfo game={game} />
              </Box>
            )}
          </StyledHoverWrapper>
        </StyledImgWrapper>
      </StyledWrapper>
    </StyledLink>
  )
}
const StyledShareButton = styled(Button)`
  &:hover {
    svg {
      fill: ${({ theme }) => theme.colors.text};
    }
  }
`

const StyledLink = styled(Link)`
  cursor: pointer;
  &:hover {
    * {
      fill: #000;
    }

    ${StyledShareButton} {
      * {
        fill: ${({ theme }) => theme.colors.text};
      }
    }
  }
`
const StyledImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const StyledHoverWrapper = styled(RowCenter)`
  z-index: 1;
  top: 0px;
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(22, 28, 36, 0) -22.78%, #191d25e0 55%, #17181b 86.75%);
`

const StyledWrapper = styled(Box)`
  cursor: pointer;
  position: relative;

  scroll-snap-align: start;
  scroll-snap-stop: normal;

  img {
    transition: ${({ theme }) => theme.transitions.medium};
  }
`

const StyledImgWrapper = styled(Box)`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radii.small};
`

const StyledPlayGame = styled(Box)`
  background: ${({ theme: { colors } }) => colors.text};
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme: { radii } }) => radii.tiny} !important;

  svg {
    fill: #000 !important;
    opacity: 1;
  }
`

const StyledWrapInfoGame = styled(Column)`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  justify-content: flex-end;
`

export default GameItem
